import * as WorldMapData from '@highcharts/map-collection/custom/world.geo.json';
import * as widgetActions from '../../../actions/widget.actions';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  take,
  tap,
} from 'rxjs';
import {
  DataObject,
  Feed,
  FilterItem,
  ObjectDefinition,
  ObjectPermission,
  PeriodFilterItem,
  Post,
  User,
  Widget,
} from '../../../models/core.models';
import { DialogCloseReason, DialogService } from '@sui/dialog';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';
import {
  NomLanguages,
  NomObjectClass,
  NomObjectTypes,
  PopupType,
  WidgetType,
} from '../../../constants';
import { Store, select } from '@ngrx/store';
import {
  WidgetExportFormat,
  WidgetService,
} from '../../../services/widget.service';
import {
  activateFilters,
  deselectFilterItem,
  selectFilterItem,
  toggleFilterDimension,
} from 'src/app/core/actions/feed.actions';
import { endOfDay, startOfDay } from 'date-fns';
import {
  getContentFilters,
  selectCurrentObject,
  selectFeedActiveFilterItems,
  selectObjectById,
  selectUser,
  selectWidgetViewConfig,
} from 'src/app/core/reducers';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import { DialogCloseOptions } from '@sui/dialog';
import { Highcharts } from '../../../highcharts-shared';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { MoveCopyWidgetDialogComponent } from '../../../../shared/components/move-copy-widget-dialog/move-copy-widget-dialog.component';
import {
  SwapWidgetDialogComponent,
  SwapWidgetDialogOutput,
} from '../../popups/swap-widget-dialog/swap-widget-dialog.component';
import { ObjectActionsService } from 'src/app/core/services/object-actions.service';
import { ObjectEditorService } from '../../../services/object-editor.service';
import { ObjectService } from 'src/app/core/services/object.service';
import { PickerDataobjectsDialogComponent } from '../../popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';
import { Router } from '@angular/router';
import { SidebarButtonConfig } from '../../sidebar/sidebar.component';
import { SidebarService } from 'src/app/core/services/sidebar.service';
import { TranslateService } from '@ngx-translate/core';
import { UserFacadeService } from '@services/user-facade.service';
import { UtilsService } from 'src/app/core/services/utils.service';
import { WidgetOperation } from '../../../models/widget-operations';
import { WidgetViewProperties } from 'src/app/core/services/feed.service';
import { readableTextColor } from 'src/app/shared/utils/color-utils';
import { selectSelectedObjects } from './../../../reducers/index';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { widgetMassOperation } from '../../../actions/widget.actions';

interface CardTool {
  icon: IconProp;
  tooltip?: string;
  action: () => void;
  permission: AppFeatures;
  objectPermission?: ObjectPermission[];
}

interface DropdownItem {
  id?: string;
  name?: string;
  icon?: IconProp;
  action?: () => void;
  permission?: AppFeatures;
  objectPermission?: ObjectPermission[];
  type?: 'export-option' | 'tool';
  isTitle?: boolean;
  isSeparator?: boolean;
}

@Component({
  selector: 'app-widget-card',
  templateUrl: './widget-card.component.html',
  styleUrls: ['./widget-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WidgetCardComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('widgetContent', { static: false }) widgetContent: ElementRef;

  @Input() widget: Widget;
  @Input() isPlaceholderSelected: boolean;
  @Input() widgetPreview: boolean = false;
  @Input() currentObject: DataObject;
  @Input() objectActionService: ObjectActionsService;
  @Input() editMode: boolean = true;
  @Input() user: User;
  @Input() viewProperties: WidgetViewProperties;
  @Input() presentationModeActive: boolean = false;
  @Input() resizingWidgetId$: BehaviorSubject<string | null>;
  @Input() isDragging$: BehaviorSubject<boolean>;
  @Input() searchString: string = '';
  @Input() postCardViewConfig: PropertyViewConfig[];
  @Input() showLoading: boolean = true;

  @Output() zoomChanged = new EventEmitter<{
    zoom: number;
    center: [number, number];
  }>();

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.widgetService.widgetResized.emit();
  }

  private viewPropertiesSubject = new BehaviorSubject<
    WidgetViewProperties | undefined
  >(undefined);
  readonly viewProperties$ = this.viewPropertiesSubject.asObservable();

  readonly showPivots$ = combineLatest([
    this.viewProperties$.pipe(map((vp) => vp?.widget_display_attached_pivots)),
    this.store.select((state) => {
      const widget = state.widget.widgets.get(this.widget?.id);
      return widget?.chart_type?.is_pivotable;
    }),
  ]).pipe(
    map(
      ([displayPivots, isPivotable]) => displayPivots && isPivotable === true,
    ),
  );

  readonly showFilters$ = this.viewProperties$.pipe(
    map((vp) => vp?.widget_display_attached_filters),
  );

  readonly showDataSources$ = this.viewProperties$.pipe(
    map((vp) => vp?.widget_display_attached_data_sources),
  );

  readonly hasDatasources$ = new BehaviorSubject<boolean>(false);
  readonly hasFilters$ = new BehaviorSubject<boolean>(false);
  readonly hasPivots$ = new BehaviorSubject<boolean>(false);
  readonly datasources$ = new BehaviorSubject<any[]>([]);

  readonly showDimensions$ = combineLatest([
    this.hasFilters$,
    this.hasPivots$,
    this.showPivots$,
    this.showFilters$,
  ]).pipe(
    map(
      ([hasFilters, hasPivots, showPivots, showFilters]) =>
        (showPivots || showFilters) && (hasFilters || hasPivots),
    ),
  );

  WidgetType = WidgetType;

  isSelected$: Observable<boolean>;

  Highcharts: typeof Highcharts = Highcharts; // required for <highcharts-chart>
  widgetTypeId: WidgetType;
  chartConstructor = 'chart'; // Map type

  widgetContentWidth: number;
  widgetContentHeight: number;

  widgetItems: Array<any> | any;
  posts: Array<Post>;
  noContentMsg = this.translateService.instant(
    'widget-card.message.no-content-matching-the-current-search-criteria',
  );

  tableData: any;
  chartOptions: any[] = []; // Chart options
  currentChartIndex = 0;
  isFiltersSelected: boolean;
  isPivotsSelected: boolean;
  chartRef: Highcharts.Chart;

  supportsDataSources = false;
  widgetViewIninitialized = false;
  canEditWidget: boolean = false;
  hasResults: boolean = true;

  get headerColor(): string | null {
    return this.widget?.settings?.widget_header_color || null;
  }

  // * readable text/icon color for the header when a custom background color is set
  get headerTextColor(): string | null {
    return this.headerColor ? readableTextColor(this.headerColor) : null;
  }

  get hasCustomPeriod(): boolean {
    return !!this.widget?.settings?.custom_period;
  }

  get hasOverrideOutputSettings(): boolean {
    return !!this.widget?.settings?.override_output_settings;
  }

  get periodTimeStart(): number | null {
    return this.widget?.settings?.feed?.content_filters?.time_start ?? null;
  }

  get periodTimeEnd(): number | null {
    return this.widget?.settings?.feed?.content_filters?.time_end ?? null;
  }

  private selectedCountryRawValue: number | string | null = null;

  private resizeObserver: any;

  isHighchart: boolean = false;
  showChart: boolean = true; // Used to force re-rendering of the chart when options change

  dropdownItems: DropdownItem[] = [
    {
      isTitle: true,
      name: this.translateService.instant('widget-card.title.actions-:'),
    },
    {
      isSeparator: true,
    },
    {
      name: this.translateService.instant('widget-card.tooltip.reload'),
      icon: ['fas', 'refresh'],
      action: () => this.reloadWidget(),
      permission: AppFeatures.WIDGET_ROOT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
    {
      name: this.translateService.instant('widget-card.tooltip.print'),
      icon: ['fas', 'print'],
      action: () => this.printWidget(),
      permission: AppFeatures.WIDGET_EXPORT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
    // {
    //   name: this.translateService.instant('widget-card.tooltip.download'),
    //   icon: ['fas', 'download'],
    //   action: () => this.downloadWidget(),
    //   permission: AppFeatures.WIDGET_EXPORT,
    //   objectPermission: ['can_view'],
    //   type: 'tool',
    // },
    {
      name: this.translateService.instant('widget-card.tooltip.export-csv'),
      icon: ['fas', 'file-csv'],
      action: () => this.exportChart('csv'),
      permission: AppFeatures.WIDGET_EXPORT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
    {
      name: this.translateService.instant(
        'widget-card.tooltip.export-to-excel',
      ),
      icon: ['far', 'file-excel'],
      action: () => this.exportChart('xlsx'),
      permission: AppFeatures.WIDGET_EXPORT,
      objectPermission: ['can_view'],
      type: 'tool',
    },
  ];
  exportOptions: DropdownItem[] = [
    { isSeparator: true },
    {
      isTitle: true,
      name: this.translateService.instant('widget-card.title.export-options-:'),
    },
    {
      isSeparator: true,
    },
    // {
    //   id: 'print',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.print',
    //   ),
    //   icon: ['fas', 'print'],
    //   type: 'export-option',
    // },
    {
      id: 'png',
      name: this.translateService.instant(
        'widget-card.chart-export-option.download-png',
      ),
      icon: ['fas', 'file-image'],
      type: 'export-option',
    },
    {
      id: 'jpeg',
      name: this.translateService.instant(
        'widget-card.chart-export-option.download-jpeg',
      ),
      icon: ['fas', 'file-image'],
      type: 'export-option',
    },
    {
      id: 'svg',
      name: this.translateService.instant(
        'widget-card.chart-export-option.download-svg',
      ),
      icon: ['fas', 'file-code'],
      type: 'export-option',
    },
    // {
    //   id: 'pdf',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.download-pdf',
    //   ),
    //   icon: ['fas', 'file-pdf'],
    //   type: 'export-option',
    // },

    // {
    //   id: 'csv',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.download-csv',
    //   ),
    //   icon: ['fas', 'file-csv'],
    //   type: 'export-option',
    // }
    // {
    //   id: 'xlsx',
    //   name: this.translateService.instant(
    //     'widget-card.chart-export-option.download-xlsx',
    //   ),
    //   icon: ['fas', 'file-excel'],
    //   type: 'export-option',
    // },
  ];

  private readonly allCardTools: CardTool[] = [
    {
      icon: ['fas', 'exchange-alt'],
      tooltip: this.translateService.instant(
        'widget-card.tooltip.swap-widget-type',
      ),
      action: () => this.swapWidget(),
      permission: AppFeatures.WIDGET_ROOT,
      objectPermission: ['can_edit'],
    },
    {
      icon: ['far', 'copy'],
      tooltip: this.translateService.instant(
        'widget-card.tooltip.copy-/-move-widget',
      ),
      action: () => this.duplicateWidget(),
      permission: AppFeatures.WIDGET_DUPLICATE,
      objectPermission: ['can_view'],
    },
    {
      icon: ['fas', 'gear'],
      tooltip: this.translateService.instant('widget-card.tooltip.settings'),
      action: () => this.editWidget(),
      permission: AppFeatures.WIDGET_DUPLICATE,
      objectPermission: ['can_edit'],
    },
    {
      icon: ['far', 'times-circle'],
      tooltip: this.translateService.instant(
        'widget-card.tooltip.remove-widget',
      ),
      action: () => this.deleteWidget(),
      permission: AppFeatures.WIDGET_ROOT,
      objectPermission: ['can_edit'],
    },
  ];

  // 2) the filtered, bindable array
  cardTools: CardTool[] = [];

  windowWidth: number = window.innerWidth;
  windowHeight: number = window.innerHeight;

  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private objectService: ObjectService = inject(ObjectService);
  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private widgetService: WidgetService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private editService: ObjectEditorService,
    private utilsService: UtilsService,
    private sidebarService: SidebarService,
    private logger: LoggerService,
    private renderer: Renderer2,
    protected userFacade: UserFacadeService,
    private feedSettingsService: FeedSettingsService,
  ) {
    this.store
      .select(selectWidgetViewConfig)
      .pipe(
        tap((config) => {
          this.isFiltersSelected = config.some(
            (item) => item.path === 'filters' && item.selected,
          );
          this.isPivotsSelected = config.some(
            (item) => item.path === 'pivots' && item.selected,
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private async initializePostCardViewConfig(): Promise<void> {
    if (this.widgetPreview) {
      return;
    }
    const user =
      this.user || (await firstValueFrom(this.store.select(selectUser)));
    const objectTypeId: number =
      this.widget?.datasources?.[0]?.object_type_id ??
      NomObjectTypes.ONLINE_FEED;
    const feedConfig = await this.feedSettingsService.getFeedConfig(
      objectTypeId,
      user,
    );
    const defaultSelected = feedConfig
      .filter((c) => c.selected)
      .map((c) => c.path);
    const selectedPaths =
      this.widget.settings?.feed?.view_properties || defaultSelected;

    this.postCardViewConfig =
      this.feedSettingsService.buildViewConfigFromSelectedPaths(
        feedConfig,
        selectedPaths,
      );
  }

  private refreshCardTools(): void {
    this.cardTools = this.allCardTools.filter(
      (tool) =>
        this.userFacade.hasAllObjectPermissionsSync(
          this.currentObject,
          tool.objectPermission ?? [],
        ) &&
        (!tool.permission ||
          this.userFacade.hasPermissionSync(tool.permission) === true),
    );

    this.dropdownItems = this.dropdownItems.filter((item) => {
      if (item.type === 'tool') {
        return (
          this.userFacade.hasAllObjectPermissionsSync(
            this.currentObject,
            item.objectPermission ?? [],
          ) &&
          (!item.permission ||
            this.userFacade.hasPermissionSync(item.permission) === true)
        );
      } else {
        return true;
      }
    });
  }

  ngOnInit() {
    this.isHighchart = this.isChartOrMap();
    if (this.widget.widget_type_id === WidgetType.WIDGET_TYPE_CHART) {
      this.exportOptions.forEach((option) => this.dropdownItems.push(option));
    }
    if (this.widget) {
      this.canEditWidget = this.userFacade.hasAllObjectPermissionsSync(
        this.currentObject,
        ['can_edit'],
      );

      this.refreshCardTools();
      const widgetTypeId = this.widget.widget_type_id;
      const mapWidgetTypesWithNoDataSources = new Set([
        WidgetType.WIDGET_TYPE_SECTION_BREAK,
        WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_SYSTEM,
        WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_USER,
      ]);

      this.supportsDataSources =
        !mapWidgetTypesWithNoDataSources.has(widgetTypeId);
    }

    this.isSelected$ = this.store.select(selectSelectedObjects).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((selectedObjects) => selectedObjects.has(this.widget.id)),
      distinctUntilChanged(),
    );

    // * Listen for resize events to hide content during resize
    if (this.resizingWidgetId$) {
      this.resizingWidgetId$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((id) => id === this.widget.id),
          distinctUntilChanged(),
          tap((isResizing) => {
            if (
              this.widgetContent &&
              // * Hide HTML/SUMMARY widgets when ANY widget is being resized
              (this.widget.widget_type_id === WidgetType.WIDGET_TYPE_HTML ||
                this.widget.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY)
            ) {
              this.widgetContent.nativeElement.style.display = isResizing
                ? 'none'
                : '';
            }
          }),
        )
        .subscribe();
    }

    // * Hide ALL HTML/SUMMARY widgets when ANY widget is being dragged
    if (this.isDragging$) {
      this.isDragging$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          distinctUntilChanged(),
          tap((isDragging) => {
            if (
              this.widgetContent &&
              isDragging &&
              (this.widget.widget_type_id === WidgetType.WIDGET_TYPE_HTML ||
                this.widget.widget_type_id === WidgetType.WIDGET_TYPE_SUMMARY)
            ) {
              this.widgetContent.nativeElement.style.display = 'none';
            } else if (this.widgetContent && !isDragging) {
              this.widgetContent.nativeElement.style.display = '';
            }
          }),
        )
        .subscribe();
    }

    // * listen for Country Filter removal
    if (this.widgetTypeId === WidgetType.WIDGET_TYPE_GEO_MAP) {
      this.store
        .select(selectFeedActiveFilterItems)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((activeFilters) => {
            const hasCountryFilter = activeFilters.some(
              (filter) => filter.property_uri === 'post_section_country_id',
            );

            // * if no Country Filter exists -> reset our state
            if (!hasCountryFilter) {
              this.selectedCountryRawValue = null;
            }
          }),
        )
        .subscribe();
    }

    //this.initializeFeedViewConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['viewProperties']) {
      // this.logger.debug(
      //   'WidgetCardComponent: viewProperties changed',
      //   changes['viewProperties'].currentValue,
      // );
      this.viewPropertiesSubject.next(this.viewProperties);
    }

    if (changes['postCardViewConfig']) {
      this.postCardViewConfig = changes['postCardViewConfig'].currentValue;
    }

    const widgetChange = changes['widget'];

    if (!widgetChange) {
      this.logger.debug(`WidgetCardComponent: no widget change`, changes);
      return;
    }

    const currentWidget = widgetChange.currentValue as Widget;

    // Update our observables when widget changes
    this.hasDatasources$.next(currentWidget?.datasources?.length > 0);
    this.hasFilters$.next(currentWidget?.dimensions_filter?.length > 0);
    this.hasPivots$.next(currentWidget?.dimensions_pivot?.length > 0);
    this.datasources$.next(currentWidget?.datasources || []);

    const previousWidget = widgetChange.previousValue as Widget;

    if (
      widgetChange.firstChange ||
      currentWidget?.data !== previousWidget?.data ||
      currentWidget?.widget_type_id !== previousWidget?.widget_type_id ||
      currentWidget?.chart?.id !== previousWidget?.chart?.id ||
      currentWidget?.chart_type?.id !== previousWidget?.chart_type?.id
    ) {
      this.logger.debug('widget.data changed');
      this.widgetTypeId = currentWidget.widget_type_id;
      this.isHighchart = this.isChartOrMap();

      if (currentWidget?.data) {
        this.handleDataChange(currentWidget.data);
      }
      // reset view config to trigger re-evaluation of applicable properties in settings popup

      this.initializePostCardViewConfig();
    }

    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    this.widgetViewIninitialized = true;

    // * if we have HTML Widget data BUT no iframe was created yet -> (preview mode timing issue)
    if (
      this.widget?.data &&
      (this.widgetTypeId === WidgetType.WIDGET_TYPE_HTML ||
        this.widgetTypeId === WidgetType.WIDGET_TYPE_SUMMARY) &&
      this.widgetContent?.nativeElement &&
      !this.widgetContent.nativeElement.querySelector(
        'iframe.html-widget-iframe',
      )
    ) {
      // * retry iframe creation now that ViewChild is available
      this.handleDataChange(this.widget.data);
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        this.updateChartDimensions(width, height);
      }
    });

    if (this.widgetContent?.nativeElement) {
      this.resizeObserver.observe(this.widgetContent.nativeElement);
    }

    this.widgetService.widgetResized
      .pipe(
        debounceTime(200),
        tap(() => {
          if (this.widget.is_visible && this.chartRef) {
            this.chartRef?.reflow();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private updateChartDimensions(width?: number, height?: number) {
    if (!this.chartRef || !this.isHighchart) return;

    // * if dimensions not provided -> get them from the Element
    if (!width || !height) {
      const rect = this.widgetContent.nativeElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }

    // * apply minimum dimensions
    width = Math.max(200, Math.floor(width));
    height = Math.max(200, Math.floor(height));

    // * update chart dimensions in a safe way - guard against timing issues
    try {
      if (this.chartRef && typeof this.chartRef.setSize === 'function') {
        this.chartRef.setSize(width, height, false);
      }
    } catch (error) {
      // ! chart might have been destroyed, clear the reference
      this.chartRef = null;
      return;
    }

    // * trigger redraw
    requestAnimationFrame(() => {
      // * guard against timing issues
      try {
        if (this.chartRef && typeof this.chartRef.reflow === 'function') {
          this.chartRef.reflow();
        }
      } catch (error) {
        // ! chart might have been destroyed during the requestAnimationFrame
        this.chartRef = null;
      }
    });
  }

  printWidget(): void {
    const printContent = this.widgetContent.nativeElement.innerHTML;

    const width = 900;
    const height = 700;

    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const printWindow = window.open(
      '',
      'printWindow',
      `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );

    if (!printWindow) return;

    const styles = Array.from(
      document.querySelectorAll('style, link[rel="stylesheet"]'),
    )
      .map((el) => el.outerHTML)
      .join('\n');

    printWindow.document.write(`
    <html>
      <head>${styles}</head>
      <body>${printContent}</body>
    </html>
  `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 150);
  }

  downloadWidget() {
    this.exportChart('pdf');
  }

  exportToExcel() {
    this.exportChart('xlsx');
  }

  configurePopup(
    objectTypeIds: NomObjectTypes[],
    selectedObjectIds: Map<string, ObjectDefinition>,
    onResolvedAction: (objectIds: string[]) => any,
    popupType: PopupType,
  ) {
    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: { objectTypeIds, selectedObjectIds, popupType },
        height: '70vh',
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.subscribe((event: any) => {
      if (
        event.reason === DialogCloseReason.Save ||
        event.reason === DialogCloseReason.SaveAndClose
      ) {
        this.store.dispatch(onResolvedAction(event?.data));
      }

      console.log(
        '%c Dialog closed with event:',
        'color: green; font-weight: bold;',
        event,
      );
    });
  }

  editFilter(dimension: Feed) {
    this.editService.loadFeedAndOpenPopup(dimension.id, 'search-builder');
  }

  configureFilters() {
    const objectMap = new Map<string, ObjectDefinition>(
      this.widget.dimensions_filter?.map((object) => [
        object.id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]),
    );

    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      objectMap,
      (objectIds) =>
        widgetActions.applyWidgetFilterIds({
          widgetId: this.widget.id,
          filterIds: objectIds,
        }),
      PopupType.ATTACH_DIMENSIONS_AS_FILTERS,
    );
  }

  configurePivots() {
    const objectMap = new Map<string, ObjectDefinition>(
      this.widget.dimensions_pivot?.map((object) => [
        object.id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]),
    );

    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      objectMap,
      (objectIds) =>
        widgetActions.applyWidgetPivotIds({
          widgetId: this.widget.id,
          pivotIds: objectIds,
        }),
      PopupType.ATTACH_DIMENSIONS_AS_PIVOTS,
    );
  }

  configureDatasourceObject(datasource: Feed) {
    this.editService.loadFeedAndOpenPopup(datasource.id, 'search-builder');
  }

  // * this is bound to the button for "noDatasources"
  configureDatasource() {
    const objectMap = new Map<string, ObjectDefinition>(
      this.widget.datasources?.map((object) => [
        object.object_id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]),
    );

    this.configurePopup(
      [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.COMBINED_FEED,
      ],
      objectMap,
      (objectIds) =>
        widgetActions.applyWidgetDatasourceIds({
          widgetId: this.widget.id,
          datasourceIds: objectIds,
          datasourceClassIds: Array(objectIds?.length).fill(
            NomObjectClass.FEED,
          ),
        }),
      PopupType.SELECT_DATASOURCES,
    );
  }

  isChartOrMap(): boolean {
    return (
      this.widgetTypeId === WidgetType.WIDGET_TYPE_CHART ||
      this.widgetTypeId === WidgetType.WIDGET_TYPE_GEO_MAP
    );
  }

  chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chartRef = chart;
    this.scheduleChartResize();
  };

  private scheduleChartResize(): void {
    requestAnimationFrame(() => {
      const rect = this.widgetContent?.nativeElement?.getBoundingClientRect();
      if (rect) {
        this.updateChartDimensions(rect.width, rect.height);
      }
    });
  }

  private resetWidgetState(): void {
    this.chartOptions = [];
    this.currentChartIndex = 0;
    this.chartConstructor = 'chart';
    this.chartRef = null;
    this.tableData = null;
    this.posts = null;
    this.removeIframe();
  }

  private handleDataChange(data: any): void {
    // Handle the change in data here
    const widgetContent = atob(data);
    const widgetContentJson = JSON.parse(widgetContent);
    this.hasResults = widgetContentJson.has_results ?? false;

    this.resetWidgetState();
    this.widgetItems = widgetContentJson.widget_data;

    switch (this.widgetTypeId) {
      case WidgetType.WIDGET_TYPE_CHART:
        this.processChartWidget(this.widgetItems);
        break;

      case WidgetType.WIDGET_TYPE_TABLE:
        //print the table in the console
        //console.log('table', this.widgetItems);
        this.tableData = this.widgetItems;
        break;

      case WidgetType.WIDGET_TYPE_GEO_MAP:
        this.initializeMap(this.widgetItems[0].data);
        break;
      case WidgetType.WIDGET_TYPE_POST_LIST:
        this.posts = this.widgetItems[0].posts;
        break;
      case WidgetType.WIDGET_TYPE_SUMMARY:
      case WidgetType.WIDGET_TYPE_HTML:
        if (this.hasResults) {
          this.createIframe();
        } else {
          this.removeIframe();
        }
        break;
    }
  }

  private removeIframe(): void {
    // Find an existing iframe if it exists

    const existingIframe = this.widgetContent?.nativeElement.querySelector(
      'iframe.html-widget-iframe',
    ) as HTMLIFrameElement | null;
    if (existingIframe?.parentNode) {
      existingIframe.parentNode.removeChild(existingIframe);
    }
  }

  private createIframe(): void {
    // Find an existing iframe if it exists

    if (!this.widgetContent) {
      // No widget content element found and hence no iframe can be created
      // * In Preview Mode, ViewChild might not be ready yet - defer iframe creation
      if (this.widgetViewIninitialized) {
        // * ViewChild should be available by now, if isn't - clear data to retry
        this.widget.data = '';
      }
      // For preview mode, don't clear data - let ngAfterViewInit handle it
      return;
    }
    const existingIframe = this.widgetContent.nativeElement.querySelector(
      'iframe.html-widget-iframe',
    );

    let failOverHTML = '';
    if (!this.widgetItems?.[0]?.html) {
      const failOverMessage = this.translateService.instant(
        'widget.message.no-content-available',
      );
      failOverHTML =
        '<span class="widget-no-content"><p>' + failOverMessage + '</p></span>';
    }

    if (existingIframe) {
      // Update the srcdoc of the existing iframe
      existingIframe.srcdoc = this.widgetItems?.[0]?.html ?? failOverHTML;
    } else {
      // Create a new iframe element if it doesn't exist
      const iframe = this.renderer.createElement('iframe');
      iframe.className = 'html-widget-iframe';

      // Set sandbox attributes for security
      iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-popups allow-forms',
      );

      // Write the widgetHtml into the iframe
      iframe.srcdoc = this.widgetItems?.[0]?.html ?? failOverHTML;

      // Inject the iframe into the container
      const container = this.widgetContent.nativeElement;
      container.appendChild(iframe);
    }
  }
  stringifyWithFunctions(obj) {
    return JSON.stringify(obj, (key, value) => {
      // If the value is a function, convert it to its string representation
      if (typeof value === 'function') {
        return value.toString();
      }
      return value;
    });
  }

  private processChartWidget(widgetItems: any[]): void {
    if (!this.utilsService.isIterable(widgetItems)) {
      return;
    }
    this.chartOptions = [];

    if (this.widgetPreview) {
      // destroy previous chart instance to prevent memory leaks and stale charts in preview mode
      this.updateChart();
    }

    for (const item of widgetItems) {
      if (typeof item.chart === 'undefined') {
        continue;
      }

      const chartOptions = atob(item.chart);
      this.store.dispatch(
        widgetActions.widgetUpdateChartOptions({
          chartOptions,
          widgetId: this.widget.id,
        }),
      );

      this.widget = {
        ...this.widget,
        chart_options: chartOptions,
      };

      let chartDataParsed = {};
      try {
        eval('chartDataParsed = ' + chartOptions + ';');
      } catch (error) {
        this.logger.error('Error parsing chart options:', error, chartOptions);
      }

      // Merge base responsive options
      const responsiveOptions = {
        chart: {
          backgroundColor: 'transparent',
          reflow: true,
          animation: false,
          spacing: [20, 20, 20, 20],
        },
      };

      chartDataParsed = {
        ...chartDataParsed,
        chart: {
          ...chartDataParsed['chart'],
          ...responsiveOptions.chart,
        },
      };

      this.chartOptions.push(chartDataParsed);
    }

    // Apply series click and xAxis zoom handlers to all chartOptions
    this.chartOptions.forEach((chart) => {
      if (chart?.series && !this.widgetPreview) {
        chart.series.forEach((series) => {
          series.point = {
            events: {
              click: this.onPointClick.bind(this),
            },
          };
        });
      }

      if (chart?.xAxis) {
        const xAxes = Array.isArray(chart.xAxis) ? chart.xAxis : [chart.xAxis];

        xAxes.forEach((axis: any) => {
          axis.events = {
            afterSetExtremes: (
              event: Highcharts.AxisSetExtremesEventObject,
            ) => {
              if (event.trigger === 'zoom') {
                const isResetZoom =
                  typeof event.userMin === 'undefined' &&
                  typeof event.userMax === 'undefined';

                const startTimestamp = Math.floor(
                  (isResetZoom ? event.dataMin : event.userMin) / 1000,
                );
                const endTimestamp = Math.floor(
                  (isResetZoom ? event.dataMax : event.userMax) / 1000,
                );

                const chart = (event.target as any).chart;
                const seriesPoint = chart.series?.[0]
                  ?.data?.[0] as Highcharts.Point;

                const options = JSON.parse(
                  JSON.stringify(seriesPoint?.options ?? {}),
                );

                this.store.dispatch(
                  widgetActions.widgetPointClicked({
                    widgetId: this.widget.id,
                    options: {
                      ...options,
                      zoom_start: startTimestamp,
                      zoom_end: endTimestamp,
                      is_reset_zoom: isResetZoom,
                    },
                  }),
                );
              }
            },
          };
        });
      }
    });
  }

  onPointClick(event: Highcharts.PointClickEventObject): void {
    this.handlePointClick(event);
  }

  private handlePointClick(event: Highcharts.PointClickEventObject): void {
    if (!event?.point?.options) return;

    // Deep clone to break the reference to Highcharts' internal object
    const options = JSON.parse(JSON.stringify(event.point.options));

    this.store.dispatch(
      widgetActions.widgetPointClicked({
        widgetId: this.widget.id,
        options,
      }),
    );
  }

  private loadWidgetData(): void {
    if (
      this.widgetTypeId !== this.WidgetType.WIDGET_TYPE_PLACEHOLDER &&
      this.widgetPreview === false &&
      this.widget.id
    ) {
      const pageId = this.editMode === false ? this.widget.page_id : undefined;

      this.store.dispatch(
        widgetActions.loadWidget({
          widgetId: this.widget.id,
          // * only pass pageId in Presentation Mode (when editMode is false)
          ...(pageId && { pageId }),
        }),
      );
    }
  }

  toggleWidgetSelect() {
    this.objectActionService.toggleObjectSelection(this.widget);
  }

  updateWidgetHeaderColor(color: string | null) {
    const widgetCopy = {
      ...this.widget,
      settings: {
        ...this.widget.settings,
        widget_header_color: color,
      },
    };

    this.store.dispatch(
      widgetActions.updateWidget({ widgetId: this.widget.id, data: widgetCopy }),
    );
  }

  getSavedZoom(): { zoom: number; center: [number, number] } | null {
    const zoom = this.widget.settings?.['map_zoom'];
    const center = this.widget.settings?.['map_center'];

    if (
      typeof zoom === 'number' &&
      Array.isArray(center) &&
      center.length === 2 &&
      typeof center[0] === 'number' &&
      typeof center[1] === 'number'
    ) {
      return {
        zoom,
        center: center as [number, number],
      };
    }

    return null;
  }

  initializeMap(data: any[]) {
    // Prepare data for Highcharts
    const highchartsData = data.map((item) => {
      return {
        'hc-key': item.code.toLowerCase(), // Highcharts uses lowercase country codes
        value: item.y,
        raw: item.raw,
      };
    });

    this.chartConstructor = 'mapChart'; // ! Set the chart type to 'Map'

    const baseOptions = {
      chart: {
        map: WorldMapData,
        backgroundColor: 'transparent',
        reflow: true,
        spacing: [20, 20, 20, 20],
        events: {
          load: () => {
            const savedZoom = this.getSavedZoom();
            const mapChart = this.chartRef as Highcharts.MapChart;

            if (savedZoom && mapChart.mapView) {
              this.logger.debug('Restoring map zoom:', savedZoom);

              try {
                // ! clone "center" to prevent Highcharts from mutating a Frozen array
                const [x, y] = savedZoom.center;
                const safeCenter: [number, number] = [x, y];

                mapChart.mapView.setView(safeCenter, savedZoom.zoom);
              } catch (err) {
                this.logger.error('Failed to restore map zoom:', err);
              }
            }

            // * trigger initial size calculation after map is loaded
            setTimeout(() => {
              if (this.widgetContent?.nativeElement) {
                const rect =
                  this.widgetContent.nativeElement.getBoundingClientRect();
                this.updateChartDimensions(rect.width, rect.height);
              }
            }, 0);
          },
          redraw: () => {
            const mapChart = this.chartRef as Highcharts.MapChart;
            const mapView = mapChart.mapView;

            if (mapView) {
              const zoom = mapView.zoom;
              const center = mapView.center as [number, number];

              this.zoomChanged.emit({ zoom, center });
            } else {
              this.logger.warn('MapView not available during redraw');
            }
          },
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      animation: false,
      exporting: {
        enabled: false,
      },
      navigation: {
        buttonOptions: {
          enabled: true,
        },
      },
      mapNavigation: {
        enabled: true,
        enableDoubleClickZoom: true,
        enableMouseWheelZoom: true,
        enableTouchZoom: true,
        buttonOptions: {
          align: 'right',
        },
      },
      series: [
        {
          data: highchartsData,
          animation: false,
          mapData: WorldMapData,
          joinBy: 'hc-key',
          nullColor: '#D7D7D7', // Color for countries with no data
          name: '',
          states: {
            hover: {
              color: '#BADA55',
            },
          },
          dataLabels: {
            enabled: true,
            formatter: function () {
              if (
                typeof this.point.value != 'undefined' &&
                this.point.value != null
              ) {
                return (
                  this.point['hc-key'] + ': ' + number_format(this.point.value)
                );
              }

              return this.point.value;
            },
          },
          legend: {
            layout: 'vertical',
            align: 'left',
            verticalAlign: 'top',
            borderRadius: 5,
          },
          point: {
            events: {
              click: (event: any) => {
                // * if already selected -> do nothing
                if (this.selectedCountryRawValue !== null) {
                  return;
                }

                const propertyUri = 'post_section_country_id';
                const name = (event.point as any).name;
                const rawValue = (event.point.options as any).raw;

                this.selectedCountryRawValue = rawValue;

                const myFilterItem: FilterItem = {
                  title: name,
                  kind: 'value',
                  raw_value: rawValue,
                  count_posts: 0,
                  icon: 'filter_icon',
                  checked: true,
                  active: false,
                  property_uri: propertyUri,
                  type: 'include',
                };

                this.store.dispatch(
                  selectFilterItem({
                    propertyUri: propertyUri,
                    filterItem: myFilterItem,
                    mode: 'include',
                  }),
                );

                this.store.dispatch(activateFilters());
              },
            },
          },
        },
      ],
    };

    this.chartOptions = [baseOptions];
  }

  deleteWidget() {
    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {},
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(
            widgetActions.deleteWidget({ widgetId: this.widget.id }),
          );
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  editWidget() {
    this.editService.editWidget(this.widget);
  }

  swapWidget() {
    const dialogRef = this.dialogService.openSimpleDialog(
      SwapWidgetDialogComponent,
      {
        data: {
          widget: this.widget,
          objectTypeId: this.currentObject.object_type_id,
          objectClassId: this.currentObject.object_class_id,
        },
        height: '70vh',
        width: '50vw',
        minWidth: 800,
        maxWidth: 1200,
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.pipe(take(1)).subscribe((event: any) => {
      if (event.reason === DialogCloseReason.Confirm && event.data) {
        const result = event.data as SwapWidgetDialogOutput;
        this.store.dispatch(
          widgetActions.swapWidgetType({
            widgetId: this.widget.id,
            chartId: result.chartId,
            chartTypeId: result.chartTypeId,
          }),
        );
      }
    });
  }

  onConfirm() {}

  onCancel() {}

  reloadWidget() {
    if (this.widget.id) {
      this.store.dispatch(
        widgetActions.loadWidget({ widgetId: this.widget.id }),
      );
    }
  }

  // * widget is scrolled into view

  handleVisibilityChange(isVisible: boolean): void {
    // * skip visibility tracking in Presentation Mode -> data is prefetched
    if (this.presentationModeActive) {
      return;
    }

    if (this.widget && this.widget.id) {
      this.store.dispatch(
        widgetActions.widgetVisibilityChanged({
          widgetId: this.widget.id,
          isVisible: isVisible,
        }),
      );
    }
    if (!this.widget.is_loaded && isVisible) {
      this.loadWidgetData();
    }
  }

  onItemClick(item: DropdownItem) {
    if (item.type === 'export-option') {
      this.exportChart(item.id);
    } else {
      item.action();
    }
  }

  private handleExport(exportFormat: WidgetExportFormat) {
    this.store
      .select(getContentFilters)
      .pipe(take(1))
      .subscribe((filters) => {
        this.widgetService
          .exportWidget(
            this.widget.id,
            filters,
            NomLanguages.ENGLISH, // language can be dynamic based on user preference
            exportFormat,
          )
          .subscribe({
            next: (fileData: any) => {
              this.triggerDownload(
                fileData,
                `${this.widget.title}.${exportFormat}`,
              );
            },
            error: (err) => console.error('Export failed:', err),
          });
      });
  }

  private triggerDownload(data: any, fileName: string) {
    // 1. Extract extension (e.g., 'path/to/file.pdf' -> 'pdf')
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    // 2. Get the correct MIME type
    const mimeType = this.getMimeType(extension);

    // 3. Create the Blob with the dynamic type
    const blob = new Blob([data], { type: mimeType });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Important: Free up memory
  }

  private getMimeType(extension: string) {
    const types: Record<string, string> = {
      csv: 'text/csv;charset=utf-8;',
      pdf: 'application/pdf',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      svg: 'image/svg+xml',
    };
    // Fallback to octet-stream (generic binary) if extension is unknown
    return types[extension] || 'application/octet-stream';
  }

  exportChart(fileType: string) {
    switch (fileType) {
      case 'print':
        this.printWidget();
        break;
      case 'pdf':
      case 'png':
      case 'svg':
        this.chartRef?.exportChart(
          {
            type: this.getMimeType(fileType) as any,
            filename: this.widget.title,
          },
          {},
        );
        break;
      case 'jpeg':
        this.chartRef?.update({ chart: { backgroundColor: 'white' } });
        this.chartRef?.exportChart(
          {
            type: 'image/jpeg',
            filename: this.widget.title,
          },
          {},
        );
        break;
      case 'csv':
        this.handleExport('csv');
        break;
      case 'xlsx':
        this.handleExport('xlsx');
        break;
    }
  }

  shouldHideMessage(typeId: WidgetType): boolean {
    return (
      typeId === WidgetType.WIDGET_TYPE_CSV ||
      typeId === WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_USER ||
      typeId === WidgetType.WIDGET_TYPE_EXPORT_VARIABLES_SYSTEM ||
      typeId === WidgetType.WIDGET_TYPE_SECTION_BREAK ||
      typeId === WidgetType.WIDGET_TYPE_PLACEHOLDER
    );
  }

  duplicateWidget() {
    this.store
      .pipe(
        select(selectCurrentObject),
        take(1),
        tap((currentObject) => {
          if (!currentObject) {
            return;
          }

          this.showWidgetDialog();
        }),
      )
      .subscribe();
  }

  // * Show the new dashboard widget dialog for single widget
  private showWidgetDialog(): void {
    this.store
      .pipe(
        select(selectCurrentObject),
        take(1),
        tap((currentObject) => {
          const dialogRef = this.dialogService.openSimpleDialog(
            MoveCopyWidgetDialogComponent,
            {
              data: {
                widgets: [this.widget],
                currentObject: currentObject,
              },
              closeOnClick: false,
            },
          );

          dialogRef.closed
            .pipe(take(1))
            .subscribe(
              (
                result: DialogCloseOptions<
                  { data: WidgetOperation[] } | undefined
                >,
              ) => {
                if (
                  result.reason === DialogCloseReason.Save &&
                  result.data?.data
                ) {
                  this.store.dispatch(
                    widgetMassOperation({ operations: result.data.data }),
                  );
                }
              },
            );
        }),
      )
      .subscribe();
  }

  changeIndex(index: number) {
    this.currentChartIndex = index;
  }

  selectWidgetPlaceholder() {
    let button: SidebarButtonConfig = {
      label: this.translateService.instant('feed-actions.label.widgets'),
      key: 'widgets',
      componentName: 'WidgetSidebarComponent',
      header: 'Widgets',
      icon: ['fas', 'chart-bar'],
    };
    this.sidebarService.selectComponent(button, true);

    this.store.dispatch(
      widgetActions.selectWidgetPlaceholderId({ id: this.widget.id }),
    );
  }

  removeFilter(dimension: Feed, $event: MouseEvent) {
    $event.stopPropagation();

    this.store.dispatch(
      widgetActions.removeWidgetFilter({
        widgetId: this.widget.id,
        filterId: dimension.id,
      }),
    );
  }

  removePivot(dimension: Feed, $event: MouseEvent) {
    $event.stopPropagation();

    this.store.dispatch(
      widgetActions.removeWidgetPivot({
        widgetId: this.widget.id,
        pivotId: dimension.id,
      }),
    );
  }

  // In your component
  updateChart() {
    this.showChart = false; // Destroy the component
    this.cdr.detectChanges(); // Ensure the DOM updates

    this.showChart = true; // Re-create the component
  }
}
